import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Grid, Card, Typography, Button, TablePagination, ButtonBase, Toolbar, CardContent, MenuItem } from '@mui/material';
import Moment from 'moment';
import SearchBox from '../../components/searchbox';
import axios from 'axios';
import Config from '../../config';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import TextField from '../../components/text_field';
import SelectField from '../../components/select_field';
import { ResponsiveBar } from '@nivo/bar';
import './scorecards.css';
import FileSaverBase from 'filesaver.js-npm';
import { dollarFormat, formatHoursWithDays, hoursToDays } from '../../helpers';
import { Link } from 'react-router-dom';
import Auth from '../../auth';
import createDecorator from 'final-form-calculate';
import { utilGetPartners } from "../../utils/utils";
import FastSelectBase from '../../components/select';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// inventory/v1/categories.tsx and testing/cases.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;
// filesaver.js-npm ships no type declarations (matches reports.tsx's cast).
const FileSaver: any = FileSaverBase;
// select.js default-exports a bare forwardRef with untyped props; the
// inferred type rejects style/isClearable/variant/etc. (same pattern as
// search.tsx/catalog_item.tsx's FastSelectBase cast).
const FastSelect: any = FastSelectBase;

// @nivo/bar ships a stale/mismatched .d.ts (e.g. colorBy declared as
// required) -- same issue worked around in catalogmanager/.../DataBox.tsx and
// reports.tsx. Cast to any at the import boundary.
const AnyResponsiveBar: any = ResponsiveBar;

// A useState paired with a ref that tracks the same value. The class version
// relied on `this.state` always reflecting the latest value immediately after
// `this.setState` (React 18 keeps the instance up to date for synchronous
// read-after-write). Functional closures don't, so the ref preserves that
// read-your-writes behavior for the imperative helpers below while the state
// value continues to drive rendering.
function useStateRef<T>(initial: T): [T, React.MutableRefObject<T>, (v: T) => void] {
    const [value, setValue] = useState<T>(initial);
    const ref = useRef<T>(initial);
    const set = (v: T) => {
        ref.current = v;
        setValue(v);
    };
    return [value, ref, set];
}

type ScoreValue = {
    Score?: number;
    LetterGrade?: string;
    TargetValue?: number;
    ActualValue?: number;
};

type ScoreProps = {
    score: ScoreValue;
};

const Score = ({ score }: ScoreProps) => (
    <div className='score'>
        <span className='letterColor' data-grade={score.LetterGrade}><sup className='letterSup'></sup></span> ({score.Score}%)
    </div>
);

type IfEqualsProps = {
    field: string;
    value: any;
    children: React.ReactNode | ((props: { v: any }) => React.ReactNode);
    style?: React.CSSProperties;
};

export const IfEquals = ({ field, children, value }: IfEqualsProps) => (
  <Field name={field} subscription={{ value: true }}>
    {({ input: { value: v } }: any) => (v === value ? typeof children === 'function' ? (children as (props: { v: any }) => React.ReactNode)({v}) : children : null)}
  </Field>
)

export const calculator = createDecorator(
    {
        field: 'termType',
        updates: (value: string, name: string, allValues: any) => {
            let format = 'YYYY-MM-DD';
            let start = null;
            let end = null;
            if(value === '7days'){
                start = Moment().add(-7, 'days');
                end = Moment();
            } else if (value === '30days'){
                start = Moment().add(-30, 'days');
                end = Moment();
            } else if (value === 'lastMonth'){
                start = Moment().add(-1, 'month').startOf('month');
                end = Moment().add(-1, 'month').endOf('month').add(1, 'days');
            } else if (value === 'thisMonth'){
                start = Moment().startOf('month');
                end = Moment();
            }
            return start && end ? {start: start.format(format), end: end.format(format)} : {};
        }
    }
)

type CancellationReasonsProps = {
    isRetailer?: boolean;
    partnerId?: string | number;
    startDate?: string;
    endDate?: string;
};

const CancellationReasons = ({ isRetailer, partnerId, startDate, endDate }: CancellationReasonsProps) => {
    const [cancelChartData, setCancelChartData] = useState<any[] | undefined>(undefined);

    // componentDidMount + componentDidUpdate(partnerId/startDate/endDate):
    // (re)load whenever those inputs change, clearing the chart first so the
    // stale chart doesn't linger during the reload.
    useEffect(() => {
        let cancelled = false;
        setCancelChartData([]);
        (async () => {
            const data = (await axios.get(Config.api + `/odata/Company/Functions.GetCancellationReasons?$orderby=Reason&isMerchant=${isRetailer}&partnerId=${partnerId}&startDate=${startDate}&endDate=${endDate}`)).data.value;
            if(!cancelled){
                setCancelChartData(data);
            }
        })();
        return () => { cancelled = true; };
    }, [isRetailer, partnerId, startDate, endDate]);

    if(!cancelChartData || cancelChartData.length === 0){
        return "";
    }
    return <Grid size={{ md: 12 }}>
    <Card>
        <CardContent>
            <Typography variant="h6" component="h2">Cancellation Reasons</Typography>
            <Typography>The chart below shows total cancelled quantity broken out by cancellation reason. These are by line, not by order so total quantity may be higher than the total number of orders.</Typography>
            <div style={{height: '350px'}}>
            <AnyResponsiveBar
                layout='vertical'
                margin={{
                "top": 30,
                "right": 130,
                "bottom": 60,
                "left": 60
                }}
                data={cancelChartData}
                indexBy="Reason"
                keys={["Quantity"]}
                colorBy={(data: any) => {
                    return "#aaa";
                }}
                animate={false}
                enableLabel={false}
                axisLeft={{
                    format: e => (Number.isInteger(e) ? e : '')
                }}
            /></div>
        </CardContent>
    </Card>
</Grid>;
};

type ScorecardFilter = {
    start: string;
    end: string;
    termType: string;
    grade: string;
};

type ScorecardsProps = {
    setTitle?: (title: string) => void;
    isRetailer?: boolean;
};

const Scorecards = ({ setTitle, isRetailer }: ScorecardsProps) => {
    const rowsPerPage = 9;
    const [, scorecardsRef, setScorecards] = useStateRef<any[]>([]);
    const [, termRef, setTerm] = useStateRef<string>('');
    const [filter, filterRef, setFilter] = useStateRef<ScorecardFilter>({
        start: Moment().add(-7, 'days').format('YYYY-MM-DD'),
        end: Moment().format('YYYY-MM-DD'),
        termType: '7days',
        grade: ''
    });
    const [group, groupRef, setGroupValue] = useStateRef<string>('');
    const [groups, groupsRef, setGroups] = useStateRef<string[]>([]);
    const [page, pageRef, setPage] = useStateRef<number>(0);
    const [selected, selectedRef, setSelectedValue] = useStateRef<any>(undefined);

    const [filtered, setFiltered] = useState<any[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [chartData, setChartData] = useState<any[]>([]);
    const [groupOptions, setGroupOptions] = useState<{ label: string; value: string }[]>([]);

    const handleChangePage = (event: any, nextPage: number) => {
        setPage(nextPage);
        updateList();
    };

    const pageSlice = (data: any[]) => {
        var start = rowsPerPage * pageRef.current;
        return data.slice(start, start + rowsPerPage);
    };

    const applyFilter = (list: any[]) => {
        list = list.filter(i => i.CompanyName && i.CompanyName.toLowerCase().indexOf(termRef.current.toLowerCase()) > -1
        && (!filterRef.current.grade || (filterRef.current.grade && i.Combined && i.Combined.LetterGrade && i.Combined.LetterGrade.indexOf(filterRef.current.grade) === 0 && !i.NoData)
        || (filterRef.current.grade === 'NA' && i.NoData)));
        if(groupRef.current && groupsRef.current.indexOf(groupRef.current) > -1){
            list = list.filter(r => r.Groups && r.Groups.indexOf(groupRef.current) > -1);
        }
        return list;
    };

    const updateList = () => {
        var filteredAll = applyFilter(scorecardsRef.current);
        setFiltered(pageSlice(filteredAll));
        setTotal(filteredAll.length);
    };

    const updateTerm = (e: React.SyntheticEvent<HTMLInputElement>) => {
        var value = e.currentTarget.value;
        if(termRef.current !== value){
            setTerm(value);
            updateList();
        }
    };

    const loadData = async () => {
        let startDate = Moment(filterRef.current.start).toISOString();
        let endDate = getEndDate();
        let partners = await utilGetPartners();
        let groups = partners.map((r: any) => r.Groups ?? []).flat();
        groups = groups.filter((r: any, i: number) => groups.indexOf(r) === i).sort();
        let groupOptions = groups.map((r: any) => ({label: r, value: r}));
        let scorecards = (await axios.get(Config.api + `/api/v1/partners/scorecards?includeBlank=true&startDate=${startDate}&endDate=${endDate}`)).data.Records;
        scorecards = scorecards.sort((a: any, b: any) => b.Combined.Score - a.Combined.Score);
        scorecards.forEach((s: any) => {
            s.VendorNumber = (((partners.find((r: any) => r.Id === s.Id) ?? {}).VendorCodes ?? []).find((r: any) => r.System === "Portal") ?? {}).Code;
            s.Groups = (partners.find((r: any) => r.Id === s.Id) ?? {}).Groups ?? [];
            s.Type = (partners.find((r: any) => r.Id === s.Id) ?? {}).Type;
        });
        let filtered = applyFilter(scorecards);
        let selected = selectedRef.current ? scorecards.find((r: any) => r.Id === selectedRef.current.Id) : undefined;
        setScorecards(scorecards);
        setFiltered(pageSlice(filtered));
        setTotal(filtered.length);
        setSelectedValue(selected);
        setGroups(groups);
        setGroupOptions(groupOptions);
    };

    useEffect(() => {
        if(setTitle) {
            setTitle('Scorecards');
        }
        (async () => {
            await loadData();
            // Auto-select the first scorecard on mount. Reading the `filtered`
            // state here would always see the mount-time closure ([]): this
            // async callback closes over the mount render, and `filtered` is a
            // const that is never reassigned. The pre-migration class relied on
            // legacy ReactDOM.render semantics (still in use -- src/index.js),
            // where `await this.setState(...)` inside loadData flushed
            // synchronously so `this.state.filtered` was populated by this
            // point and the first scorecard WAS auto-selected. To preserve that
            // behavior, recompute from the freshest data: `scorecardsRef` is
            // updated synchronously by loadData's setScorecards, so applyFilter
            // sees the loaded rows regardless of React 18 batching.
            let filteredNow = applyFilter(scorecardsRef.current);
            if(filteredNow.length > 0){
                setSelected(filteredNow[0].Id);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getEndDate = () => {
        if(filterRef.current.end === Moment(new Date()).format('YYYY-MM-DD')){
            return Moment(new Date()).toISOString();
        }
        return Moment(filterRef.current.end).toISOString();
    };

    const loadChartData = async () => {
        if(!selectedRef.current){
            return;
        }
        let startDate = Moment(filterRef.current.start).toISOString();
        let endDate = getEndDate();
        let chartData = (await axios.get(Config.api + `/odata/Company/Functions.GetOrderStatusCounts?$orderby=Date desc&isMerchant=${isRetailer}&partnerId=${selectedRef.current.Id}&startDate=${startDate}&endDate=${endDate}&timeZoneOffset=${new Date().getTimezoneOffset()}`)).data.value;
        chartData.forEach((r: any) => r.Date = Moment(r.Date).format('MM/DD'));
        if(Moment(endDate).format("HHmmss") === "000000"){ // don't include blank entry for last day in interval
            let exclude = Moment(endDate).format('MM/DD');
            chartData = chartData.filter((r: any) => r.Date !== exclude);
        }
        setChartData(chartData.map((r: any) => {return {Date: r.Date, Cancelled: r.Cancelled, Shipped: r.Shipped, "Not Shipped": r.NotShipped}}));
    };

    const abbreviate = (count: number) => {
        if (count >= 1000 && count < 100000) {
            return parseFloat((count / 1000).toFixed(1)) + "K";
        } else if (count >= 100000 && count < 1000000) {
            return (count / 1000).toFixed(0) + "K";
        } else if (count >= 1000000 && count < 100000000) {
            return parseFloat((count / 1000000).toFixed(1)) + "M";
        }
        return count;
    };

    const setSelected = (coid?: number | string) => {
        if(coid){
            if(!selectedRef.current || selectedRef.current.Id !== coid){
                setSelectedValue(scorecardsRef.current.filter((r: any) => r.Id === coid)[0]);
                setChartData([]);
                loadChartData();
            }
        }else{
            setSelectedValue(undefined);
            setChartData([]);
        }
    };

    const update = (newState: ScorecardFilter) => {
        if(newState.start !== filterRef.current.start || newState.end !== filterRef.current.end || newState.termType !== filterRef.current.termType || newState.grade !== filterRef.current.grade){
            setFilter({start: newState.start, end: newState.end, termType: newState.termType, grade: newState.grade});
            loadData();
            loadChartData();
        }
    };

    const downloadScoreCards = () => {
        var out = "Partner,AccountNumber,VendorNumber,OverallScore,CancellationPercentageScore,ShipmentPercentageScore,ShipmentTimeScore,SalesTotal,OrderCount,PercentShipped,PercentCancelled,AverageTimeToShip,AverageDaysToShip,Connection Type\r\n";
        var list = scorecardsRef.current;
        for (var i = 0; i < list.length; i++) {
            var row = list[i];
            out += ["\"" + row.CompanyName + "\"",
                row.Id,
                row.VendorNumber ? "\"" + row.VendorNumber + "\"" : "",
                row.Combined.Score,
                row.CancelPercent.Score,
                row.ShipPercent.Score,
                row.ShipmentTime.Score,
                row.OrderTotalAmount,
                row.OrderCount,
                row.ShipPercent.ActualValue,
                row.CancelPercent.ActualValue,
                row.ShipmentTime.ActualValue,
                hoursToDays(row.ShipmentTime.ActualValue) ?? "",
                row.Type ? "\"" + row.Type + "\"" : ""
            ].join(",") + "\r\n";
        }
        var blob = new Blob([out], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, `scorecards_${filterRef.current.start}_${filterRef.current.end}.csv`);
    };

    const countGrade = (grade: string, noData?: boolean) => {
        let count = scorecardsRef.current.filter(r => r.Combined && r.Combined.LetterGrade && r.Combined.LetterGrade.indexOf(grade) === 0 && r.NoData === !(!noData)).length;
        return `(${count})`;
    };

    const setGroup = (v: string) => {
        setGroupValue(v);
        updateList();
    };

    let canManageSettings = Auth.hasPermission('settings/manage');
    return (
        <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>Filters</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                    <Form onSubmit={update} initialValues={filter} decorators={[calculator]} render={({ handleSubmit, pristine, invalid }: any) => {
                          return (
                              <form onSubmit={handleSubmit} style={{display: 'flex'}}>
                                <Field style={{marginRight: '1rem'}} component={SelectField} label='Date Range' name='termType'>
                                    <MenuItem value='7days'>Past 7 days</MenuItem>
                                    <MenuItem value='30days'>Past 30 days</MenuItem>
                                    <MenuItem value='thisMonth'>This month</MenuItem>
                                    <MenuItem value='lastMonth'>Last month</MenuItem>
                                    <MenuItem value='custom'>Custom</MenuItem>
                                </Field>
                                <IfEquals style={{marginRight: '1rem'}} field='termType' value='custom'>
                                    <Field style={{marginRight: '1rem'}} component={TextField} type='date' label='Start Date' name='start'/>
                                    <Field style={{marginRight: '1rem'}} component={TextField} type='date' label='End Date' name='end'/>
                                </IfEquals>
                                <Field style={{width: '10em', marginRight: '1rem'}} component={SelectField} label='Letter Grade' name='grade' shrink='true' displayEmpty>
                                    <MenuItem value=''>Any</MenuItem>
                                    <MenuItem value='A'>A- to A+ {countGrade('A')}</MenuItem>
                                    <MenuItem value='B'>B- to B+ {countGrade('B')}</MenuItem>
                                    <MenuItem value='C'>C- to C+ {countGrade('C')}</MenuItem>
                                    <MenuItem value='D'>D- to D+ {countGrade('D')}</MenuItem>
                                    <MenuItem value='F'>F {countGrade('F')}</MenuItem>
                                    <MenuItem value='NA'>No data {countGrade('F', true)}</MenuItem>
                                </Field>
                                <div style={{alignSelf: 'center'}}>
                                    <Button variant='text' type='submit' disabled={pristine} color='primary'>Update</Button>
                                </div>
                        </form>
                    );}}/>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>
                        <span style={{flex: 1, textAlign: 'left'}}>{isRetailer ? 'Suppliers' : 'Retailers'}</span>
                        {isRetailer && canManageSettings ? <Button variant='text' component={Link} to='/scorecards/options' style={{color: 'white'}}>Scorecard Settings</Button> : ''}
                        {isRetailer && canManageSettings ? <Button variant='text' component={Link} to='/settings/sourcing' style={{color: 'white'}}>Sourcing Priority</Button> : ''}
                        <Button variant='text' onClick={downloadScoreCards} style={{color: 'white'}}>Download report</Button></Toolbar>
                    <CardContent style={{display: 'flex', flexDirection: 'row', gap: '1em'}}>
                        <div style={{flex: 1}}>
                            <SearchBox handleKeyUp={updateTerm} handleChange={updateTerm} placeholder={`Search ${isRetailer ? 'suppliers' : 'retailers'}`}/>
                        </div>
                        {groups.length > 0 && <div style={{width: '25em', display: 'flex'}}>
                            <FastSelect style={{flex: 1}} isClearable variant='outlined' value={group} placeholder={'Select group...'} onChange={setGroup} options={groupOptions}/>
                        </div>}
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }} style={{padding: '10px'}}>
            <Grid container>
            {filtered.map(item => {
                return <Grid size={{ md: 4 }} key={item.Id} style={{position: 'relative', overflow: 'hidden', padding: '5px'}} onClick={() => setSelected(item.Id)}>
                    <ButtonBase style={{width: '100%'}}><Card className={`miniScoreCard alignTop ${selected && selected.Id === item.Id ? 'selected' : ''}`}>
                    <div>
                        <div className='partnerLetter letterColor' data-grade={!item.NoData ? item.Combined.LetterGrade : ''}>{item.NoData ? <div className='noScoreData'>NO DATA</div> : ''}<sup className='letterSup'></sup></div>
                    </div>
                    <div className='inlineDiv alignTop'>
                        <div className='partnerName'>{item.CompanyName}</div>
                        <div className='partnerId'>{`Company ID: ${item.Id}`}</div>
                    </div>
                    </Card></ButtonBase>
                    {!item.NoData ? <div className='orderCount'><span>{abbreviate(item.OrderCount)}</span></div> : ''}
                </Grid>;
            })}
            </Grid>
            </Grid>
            <Grid size={{ md: 12 }}>
            <TablePagination
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                'aria-label': 'Previous Page',
              }}
              rowsPerPageOptions={[]}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
              }}
              onPageChange={handleChangePage}
            />
            </Grid>
            {selected ?
                selected.NoData ?
                <Grid size={{ md: 12 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" component="h2">{`No data is available for ${selected.CompanyName} in the current time range.`}</Typography>
                    </CardContent>
                </Card>
                </Grid>
                :
            <><Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{`Score Details for ${selected && selected.CompanyName}`}</Toolbar>
                </Card>
            </Grid>
            <Grid size={{ md: 6 }} style={{display: 'flex', flex: 1}}>
                <Card style={{flex: 1, display: 'flex'}}>
                    <CardContent style={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                        <div className='combinedScoreHeader'>Overall Score</div>
                        <div style={{height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <div className='combinedScore letterColor' data-grade={selected.Combined.LetterGrade}><sup className='letterSup'></sup></div>
                        </div>
                        <div style={{display: 'flex', minHeight: '30px'}}>
                        {selected.OrderCount ? <div className='bigOrderCount'>Orders: {Intl.NumberFormat('en-US').format(selected.OrderCount)}</div> : '' }
                        {selected.OrderTotalAmount ? <div className='bigOrderCount'>Sales: {dollarFormat.format(selected.OrderTotalAmount.toFixed(0))}</div> : ''}
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 6 }} style={{display: 'flex', flex: 1}}>
                <Card style={{flex: 1}}>
                    <CardContent>
                        <div className='scoreHolder'>
                            <div className='scoreTitle'>Shipment Time</div>
                            <Score score={selected.ShipmentTime}/>
                            <div className='scoreDetails'>
                                Target: {selected.ShipmentTime.TargetValue} hours to fully ship, Average time: {formatHoursWithDays(selected.ShipmentTime.ActualValue)}
                                {selected.ShipmentTimeWeight && <Fragment><br/>This makes up {(selected.ShipmentTimeWeight * 100).toFixed(0)}% of your total score.</Fragment>}
                            </div>
                        </div>
                        <div className='scoreHolder'>
                            <div className='scoreTitle'>Percent Shipped</div>
                            <Score score={selected.ShipPercent}/>
                            <div className='scoreDetails'>
                                Target: {selected.ShipPercent.TargetValue}% shipped, Actual: {selected.ShipPercent.ActualValue}% shipped
                                {selected.ShipPercentWeight && <Fragment><br/>This makes up {(selected.ShipPercentWeight * 100).toFixed(0)}% of your total score.</Fragment>}
                            </div>
                        </div>
                        <div className='scoreHolder'>
                            <div className='scoreTitle'>Percent Cancelled</div>
                            <Score score={selected.CancelPercent}/>
                            <div className='scoreDetails'>
                            Target: {selected.CancelPercent.TargetValue}% cancelled or less, Actual: {selected.CancelPercent.ActualValue}% cancelled
                            {selected.CancelPercentWeight && <Fragment><br/>This makes up {(selected.CancelPercentWeight * 100).toFixed(0)}% of your total score.</Fragment>}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" component="h2">Order Shipment Status</Typography>
                        <div style={{height: (chartData && chartData.length > 25 ? ((chartData.length / 25) * 350) : 350) + 'px'}}>
                        <AnyResponsiveBar
                            layout='horizontal'
                            margin={{
                            "top": 30,
                            "right": 130,
                            "bottom": 60,
                            "left": 60
                            }}
                            data={chartData}
                            indexBy="Date"
                            keys={["Shipped", "Not Shipped", "Cancelled"]}
                            animate={false}
                            colorBy={(data: any) => {
                                if(data.id === 'Shipped'){
                                    return "#448044";
                                }
                                if(data.id === 'Cancelled'){
                                    return "#aaa";
                                }
                                return "#ffa500";
                            }}
                            enableLabel={false}
                        /></div>
                    </CardContent>
                </Card>
            </Grid>
            <CancellationReasons isRetailer={isRetailer} partnerId={selected.Id} startDate={Moment(filter.start).toISOString()} endDate={getEndDate()}/>
            </>
            : ''}
        </Grid>
    );
};

export default Scorecards;
export {Scorecards, CancellationReasons};
